import { Component } from '@theme/component';

/**
 * @typedef {Object} ListStore
 * @property {string} name
 * @property {string} street
 * @property {string} postal_code
 * @property {string} city
 * @property {string} country
 * @property {string} url
 * @property {string} email
 * @property {string} phone
 * @property {string} latitude - Original string value
 * @property {string} longitude - Original string value
 * @property {number | null} lat - Parsed float or null if invalid
 * @property {number | null} lng - Parsed float or null if invalid
 * @property {boolean} hasValidCoords - True if lat/lng are within valid bounds
 */

/**
 * Store list web component for the store locator feature.
 *
 * Responsibilities:
 * 1. Listen for the `stores:ready` CustomEvent from the parent `<store-locator>` element.
 * 2. Render all 402 stores as scrollable cards with actionable contact links.
 * 3. Apply a CSS modifier class to stores with invalid/missing coordinates.
 * 4. Dispatch `store:selected` when a card is clicked (INTX-01).
 * 5. Handle incoming `store:selected` events to scroll and highlight the matching card (INTX-02).
 *
 * Consumes `event.detail.listStores` (ALL stores, including those without valid coordinates)
 * rather than `event.detail.mapStores` (filtered subset with valid coords only).
 *
 * No CSS in this file — all styling is handled in `store-locator.css` (Plan 04-02).
 *
 * @extends {Component}
 */
class StoreList extends Component {
  /**
   * Tracks the currently rendered store array for index-to-store and store-to-index lookups.
   * Updated on every render (initial + filter re-renders).
   * @type {ListStore[]}
   */
  #currentStores = [];

  /**
   * Called when the element is connected to the document's DOM.
   *
   * Registers a one-time listener for `stores:ready` on the parent `<store-locator>` element.
   * Synchronous per the Web Components spec — render happens inside the event handler.
   */
  connectedCallback() {
    super.connectedCallback();

    const storeLocator = this.closest('store-locator');
    if (!storeLocator) {
      console.error('[Store List] No parent <store-locator> element found.');
      return;
    }

    storeLocator.addEventListener(
      'stores:ready',
      (event) => {
        this.#renderCards(/** @type {CustomEvent} */ (event).detail.listStores);
      },
      { once: true }
    );

    // stores:filter fires repeatedly — re-render the list with the filtered subset.
    storeLocator.addEventListener('stores:filter', (event) => {
      this.#renderCards(/** @type {CustomEvent} */ (event).detail.listStores);
    });

    // Delegated click handler — dispatches store:selected for any card click (INTX-01).
    // Fires for no-coords cards too; the map handler silently no-ops for those.
    this.addEventListener('click', (event) => {
      const card = event.target.closest('[data-store-index]');
      if (!card) return;
      const index = parseInt(card.dataset.storeIndex, 10);
      const store = this.#currentStores[index];
      if (!store) return;
      this.dispatchEvent(new CustomEvent('store:selected', { bubbles: true, detail: { store } }));
    });

    // Handle incoming store:selected — scroll list to matching card and highlight it (INTX-02).
    // Also fires for self-dispatched events (card click path), which is correct and desired.
    storeLocator.addEventListener('store:selected', (event) => {
      this.#handleStoreSelected(/** @type {CustomEvent} */ (event).detail.store);
    });
  }

  /**
   * Renders all store cards into the component's innerHTML.
   *
   * All 402 stores are rendered at once via a single innerHTML assignment —
   * no virtual scrolling needed for this dataset size. Using `innerHTML` with
   * a joined array is intentionally preferred over `document.createElement` loops
   * for initial static renders (faster, fewer DOM mutations).
   *
   * @param {ListStore[]} listStores - All stores, including those without valid coordinates.
   */
  #renderCards(listStores) {
    this.#currentStores = listStores;
    this.innerHTML = listStores.map((store, index) => this.#buildCardHTML(store, index)).join('');
  }

  /**
   * Builds the HTML string for a single store card.
   *
   * Phone, email, and website blocks are conditionally rendered — only when the
   * respective field is truthy. Website links open in a new tab with `rel="noopener"`
   * for security.
   *
   * The `store-card--no-coords` modifier class is applied when `hasValidCoords` is false.
   * This flag is set by the data layer (store-locator.js) — we do not re-validate here.
   * CSS in Plan 04-02 uses this class for subtle muted styling as a visual indicator.
   *
   * Security note: Store data comes from Shopify metaobjects (admin-authored content, not
   * user-supplied input). HTML escaping is not required in this context.
   *
   * @param {ListStore} store - The store data to render.
   * @param {number} index - The zero-based index into #currentStores (stamped as data-store-index).
   * @returns {string} HTML string for the store card.
   */
  #buildCardHTML(store, index) {
    const modifierClass = store.hasValidCoords === false ? ' store-card--no-coords' : '';

    const phoneBlock = store.phone
      ? `<p class="store-card__phone"><a href="tel:${store.phone}">${store.phone}</a></p>`
      : '';

    const emailBlock = store.email
      ? `<p class="store-card__email"><a href="mailto:${store.email}">${store.email}</a></p>`
      : '';

    // Website link is only rendered when store.url is populated.
    const websiteBlock = store.url
      ? `<p class="store-card__website"><a href="${store.url}" target="_blank" rel="noopener">Visit website</a></p>`
      : '';

    return `<div class="store-card${modifierClass}" data-store-index="${index}">
  <strong class="store-card__name">${store.name}</strong>
  <p class="store-card__location">${store.city}, ${store.country}</p>
  ${phoneBlock}
  ${emailBlock}
  ${websiteBlock}
</div>`;
  }

  /**
   * Handles a `store:selected` event by scrolling the list to the matching card
   * and applying the `store-card--selected` highlight class.
   *
   * Uses `indexOf` for O(n) identity lookup — store object references are shared between
   * listStores and the event payload so reference equality works (RESEARCH Pitfall 3).
   *
   * `block: 'nearest'` scrolls within the `<store-list>` overflow-y container, NOT the
   * page viewport. Plan 03 gave both desktop and mobile layouts a fixed-height overflow-y
   * container for `<store-list>`, so scrollIntoView is safe on both breakpoints — it scrolls
   * within the panel, not the page viewport.
   *
   * Does NOT dispatch `store:selected` — only user actions dispatch (RESEARCH Pitfall 2:
   * event loop prevention).
   *
   * @param {ListStore} store - The selected store from event.detail.store.
   */
  #handleStoreSelected(store) {
    const index = this.#currentStores.indexOf(store);
    if (index === -1) return;
    const card = this.querySelector(`[data-store-index="${index}"]`);
    if (!card) return;
    this.querySelector('.store-card--selected')?.classList.remove('store-card--selected');
    card.classList.add('store-card--selected');
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

if (!customElements.get('store-list')) {
  customElements.define('store-list', StoreList);
}
